import React, { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import DefaultButton from '../default-button/DefaultButton';
import ArrowLeftWithBody from '../../assets/icons/ArrowLeftWithBody';
import CheckMark from '../../assets/icons/CheckMark';
import { ru } from 'date-fns/locale';
import InputField from '../input-field/InputField';
import { useHookstate } from '@hookstate/core';
import { getDateOnly, onInputChangeNative, showSnackbar } from '../../misc/utils';
import { simpleGetter } from '../../actions/common';
import { psychologistAppointmentsApi } from '../../api/api';

type CopyWeekScheduleModalProps = {
  onCopySchedule: () => void;
  onClose: () => void;
  selectedDay: Date | null;
  days: Date[];
};

const weeks = Array.from({ length: 12 }, (_, i) => i + 1).map(i => ({ label: i, value: i }));

const CopyWeekScheduleModal = ({
  onCopySchedule,
  onClose,
  selectedDay,
  days
}: CopyWeekScheduleModalProps) => {
  const localState = useHookstate({});
  const state: Record<string, any> = localState.get();
  const selectedPeriod = state.selectedPeriod || 1;
  const popupRef = useRef<HTMLDivElement>(null);
  const [period, setPeriod] = useState('Текущий семестр');
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const daysOfWeek = [
    { value: 1, label: 'Понедельник' },
    { value: 2, label: 'Вторник' },
    { value: 3, label: 'Среда' },
    { value: 4, label: 'Четверг' },
    { value: 5, label: 'Пятница' },
    { value: 6, label: 'Суббота' },
    { value: 0, label: 'Воскресенье' },
  ];
  const today = new Date();
  const isWeekCopy = !selectedDay
  let formattedToday = format(selectedDay || today, 'EEE, dd MMMM', { locale: ru });
  if (isWeekCopy) {
    formattedToday = getDateOnly(days[0]) + ' - ' + getDateOnly(days.at(-1))
  }

  const handleDaySelection = (day: number) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const handleSelectAllDays = () => {
    if (selectedDays.length === daysOfWeek.length) {
      setSelectedDays([]);
    } else {
      setSelectedDays([...daysOfWeek.map(d => d.value)]);
    }
  };

  const handleCopy = async () => {
    const response = await simpleGetter(psychologistAppointmentsApi.copyDaySchedule, {
      selected_period: selectedPeriod,
      selected_days: isWeekCopy ? [1] : selectedDays,
      copy_from: selectedDay || days[0],
      is_week: isWeekCopy
    });
    showSnackbar(response.success ? 'Успешно' : (response.message || 'Произошла ошибка'), response.success ? 'success' : 'error')
    onCopySchedule();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const validateForm = () => isWeekCopy || selectedDays.length

  return (
    <div className="p-4 w-[400px] relative font-sans" ref={popupRef}>
      <button
        className="absolute top-2 right-2 text-gray-500"
        onClick={onClose}
      >
        ×
      </button>
      <h2 className="text-[24px]">{formattedToday}</h2>
      <p className="text-sm mb-4 text-[#5F6B88]">
        Скопировать расписание {isWeekCopy ? 'этой недели' : 'этого дня'} на другие дни
      </p>

      <div className="mb-4">
        {/* <label className="block text-sm font-semibold text-[#5F6B88]">
          Период копирования
        </label>
        <select
          id="clubAdmin"
          className="border border-gray-100 rounded-md p-3 mt-2 w-full focus:outline-none bg-[#F0F3FA] text-black"
        >
          <option value="Текущий семестр">Текущий семестр</option>
          <option value="Следующий месяц">Следующий месяц</option>
          <option value="Следующая неделя">Следующая неделя</option>
        </select> */}
        <InputField
          type='select'
          id='copy-schedule-select-period'
          label='Период копирования (недель)'
          value={selectedPeriod}
          handleChange={onInputChangeNative(localState, 'selectedPeriod')}
          options={weeks}
        />
      </div>

      {!isWeekCopy && (
        <div className="mb-4 text-[#5F6B88]">
          <label className="block text-sm font-medium mb-2">Дни недели</label>
          <div className="flex items-center py-1.5 my-2 border-t border-b border-gray-200">
            <input
              type="checkbox"
              checked={selectedDays.length === daysOfWeek.length}
              onChange={handleSelectAllDays}
              className="mr-2 "
            />
            <label>Все</label>
          </div>
          {daysOfWeek.map((day) => (
            <div key={day.value} className="flex items-center mb-2">
              <input
                type="checkbox"
                checked={selectedDays.includes(day.value)}
                onChange={() => handleDaySelection(day.value)}
                className="mr-2"
              />
              <label>{day.label}</label>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-between mt-4 gap-2">
        <DefaultButton
          additionalStyle="flex-1 bg-gray-500 text-white px-4 py-3 rounded"
          buttonText="Вернуться"
          onClick={onClose}
          Icon={ArrowLeftWithBody}
        />
        <DefaultButton
          additionalStyle="flex-1 bg-blue-500 text-white px-4 py-3 rounded"
          buttonText="Скопировать"
          onClick={handleCopy}
          Icon={CheckMark}
          disabled={!validateForm()}
        />
      </div>
    </div>
  );
};

export default CopyWeekScheduleModal;
